import { fetchApi } from '@/requests/api';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { Bookmark, Folder, UserBookmarks } from '../types';
import { BOOKMARK_QUERY_KEY } from './use-get-user-bookmarks';
import {
  addBookmarkToLocation,
  addFolderToLocation,
  findSharedFolderById,
  removeSharedFolderById,
  type FolderIndexPath,
} from '../utils/bookmark-utils';
import { useSession } from '@/providers/session-provider';

export function useSaveUserBookmarks(
  options?: UseMutationOptions<
    UserBookmarks,
    Error,
    Omit<UserBookmarks, 'user_id' | 'customer_id'>
  >
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (bookmark) => {
      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user`,
        method: 'PUT',
        body: JSON.stringify(bookmark),
      });
      if (!res.ok) {
        throw new Error('Failed to create bookmark');
      }
      const data = await res.json();
      return data;
    },
    mutationKey: [BOOKMARK_QUERY_KEY],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });

      const previousBookmarks = queryClient.getQueryData<UserBookmarks>([
        BOOKMARK_QUERY_KEY,
      ]);

      queryClient.setQueryData([BOOKMARK_QUERY_KEY], variables);
      return { previousBookmarks };
    },
    onSuccess: (data) => {
      queryClient.setQueryData([BOOKMARK_QUERY_KEY], data);
      queryClient.invalidateQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });
    },
    onError: (_err, _variables, context) => {
      // Restore from context
      if (context?.previousBookmarks) {
        queryClient.setQueryData(
          [BOOKMARK_QUERY_KEY],
          context.previousBookmarks
        );
      }
    },
  });
}

export function useSaveBookmark() {
  const queryClient = useQueryClient();
  return useMutation<
    UserBookmarks,
    Error,
    Omit<Bookmark, 'order'> & {
      folderPath?: FolderIndexPath;
    }
  >({
    mutationFn: async ({ folderPath, ...bookmark }) => {
      const userBookmarks = queryClient.getQueryData<UserBookmarks>([
        BOOKMARK_QUERY_KEY,
      ]);

      const updatedBookmarks = addBookmarkToLocation(
        userBookmarks,
        bookmark,
        folderPath
      );
      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarks),
      });
      if (!res.ok) {
        throw new Error('Failed to create bookmark');
      }
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([BOOKMARK_QUERY_KEY], data);
      queryClient.invalidateQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });
    },
  });
}

export function useSaveFolder(
  options?: UseMutationOptions<
    UserBookmarks,
    Error,
    Folder & {
      folderPath?: FolderIndexPath;
    }
  >
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async ({ folderPath, ...folder }) => {
      const userBookmarks = queryClient.getQueryData<UserBookmarks>([
        BOOKMARK_QUERY_KEY,
      ]);

      const updatedBookmarks = addFolderToLocation(
        userBookmarks,
        folder,
        folderPath
      );
      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarks),
      });
      if (!res.ok) {
        throw new Error('Failed to create folder');
      }
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([BOOKMARK_QUERY_KEY], data);
      queryClient.invalidateQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });
    },
  });
}

export function useShareBookmarksFolder() {
  const {
    data: {
      session: { userId },
    },
  } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    UserBookmarks | void,
    Error,
    { folder: Folder; userIds: string[] }
  >({
    mutationFn: async ({ folder, userIds }) => {
      if (!userId) {
        throw new Error('User ID is required');
      }

      if (!folder.client_id) {
        throw new Error('Folder ID is required');
      }

      const folderSharedBy = {
        ...folder,
        shared_by: userId,
      };

      const body = {
        folders: [folderSharedBy],
        user_ids: userIds,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user/share`,
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error('Failed to share bookmarks folder');
      }

      if (res.status === 204) {
        return;
      }

      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([BOOKMARK_QUERY_KEY], data);
      queryClient.invalidateQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });
    },
  });
}

export function useDenySharedFolder() {
  const queryClient = useQueryClient();

  return useMutation<UserBookmarks, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const userBookmarks = queryClient.getQueryData<UserBookmarks>([
        BOOKMARK_QUERY_KEY,
      ]);

      if (!userBookmarks) throw new Error('No bookmarks loaded');

      const updatedFoldersSharedWithMe = removeSharedFolderById(
        userBookmarks.folders_shared_with_me,
        id
      );

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user`,
        method: 'PUT',
        body: JSON.stringify({
          ...userBookmarks,
          folders_shared_with_me: updatedFoldersSharedWithMe,
        }),
      });

      if (!res.ok) throw new Error('Failed to deny shared folder');

      return res.json();
    },

    onSuccess: (data) => {
      queryClient.setQueryData([BOOKMARK_QUERY_KEY], data);
      queryClient.invalidateQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });
    },
  });
}

export function useAcceptSharedFolder() {
  const queryClient = useQueryClient();

  return useMutation<UserBookmarks, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const userBookmarks = queryClient.getQueryData<UserBookmarks>([
        BOOKMARK_QUERY_KEY,
      ]);

      if (!userBookmarks) throw new Error('No bookmarks loaded');

      const folderToAccept = findSharedFolderById(
        userBookmarks.folders_shared_with_me,
        id
      );

      if (!folderToAccept) {
        throw new Error('Shared folder not found');
      }

      const { shared_by, ...newFolder } = folderToAccept;

      const updatedBookmarks = addFolderToLocation(
        userBookmarks,
        newFolder,
        [],
        'user'
      );

      const updatedShared = removeSharedFolderById(
        userBookmarks.folders_shared_with_me,
        id
      );

      const updatedBookmarksAndSharedWithMe = {
        ...updatedBookmarks,
        folders_shared_with_me: updatedShared,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarksAndSharedWithMe),
      });

      if (!res.ok) throw new Error('Failed to accept shared folder');

      return res.json();
    },

    onSuccess: (data) => {
      queryClient.setQueryData([BOOKMARK_QUERY_KEY], data);
      queryClient.invalidateQueries({
        queryKey: [BOOKMARK_QUERY_KEY],
      });
    },
  });
}
